import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ChevronRight } from "lucide-react";
import { getOSIcon } from "../Endpoints/helper";
import { POLICY_PROFILES_PATH } from "./helper";
import "../CloudAnalytics/CloudEntityAnalytics.css";
import "../Endpoints/DeviceDetails/DeviceDetailPage.css";
import "./PolicyProfiles.css";

interface PolicyProfileHeaderProps {
    profileName: string;
    os: string;
    section: string;
    actions?: ReactNode;
}

const getOsLabel = (os: string) => {
    const value = os.toLowerCase();
    if (value.includes("mac")) return "macOS";
    if (value.includes("linux")) return "Linux";
    return "Windows";
};

export default function PolicyProfileHeader({ profileName, os, section, actions }: PolicyProfileHeaderProps) {
    const navigate = useNavigate();
    const handleBack = () => navigate(POLICY_PROFILES_PATH);

    return (
        <div className="cea-header endpoint-detail-header">
            <div className="cea-entity-row endpoint-detail-entity-row">
                <button
                    type="button"
                    onClick={handleBack}
                    className="cea-back-icon-btn focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    aria-label="Back to policy profiles"
                    title="Back to policy profiles"
                    data-testid="button-back"
                >
                    <ArrowLeft className="w-4 h-4" />
                </button>

                <div className="cea-avatar">
                    {getOSIcon(os)}
                </div>

                <div className="cea-entity-info endpoint-detail-entity-info">
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                        <h1 className="cea-entity-name min-w-0" title={profileName} data-testid="text-profile-name">
                            {profileName}
                        </h1>
                        <span className="policy-header-section">{section}</span>
                    </div>
                    <div className="cea-entity-sub">
                        <span>{getOsLabel(os)} policy profile</span>
                    </div>
                    <nav aria-label="Breadcrumb" className="mt-3 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                        <button type="button" onClick={handleBack} className="policy-header-crumb">
                            Policy Profiles
                        </button>
                        <ChevronRight className="w-3.5 h-3.5 shrink-0" />
                        <span className="break-all">{profileName}</span>
                        <ChevronRight className="w-3.5 h-3.5 shrink-0" />
                        <span className="font-medium text-foreground" aria-current="page">{section}</span>
                    </nav>
                </div>

                {actions && <div className="endpoint-detail-actions">{actions}</div>}
            </div>
        </div>
    );
}
